import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'
import {
  MdArrowRightAlt,
  MdMessage,
  MdOutlineQueryStats,
  MdOutlineMessage,
  MdArrowUpward,
  MdArrowDownward,
  MdContentCopy,
} from 'react-icons/md'

import type { ChangeEvent, ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IUaCampaign } from '~/types/ua-campaign'

import ActionMenu from '~/components/ActionMenu'
import AppLogo from '~/components/AppLogo'
import AvatarWithCall from '~/components/AvatarWithCall'
import Badge from '~/components/Badge'
import FlagCountry from '~/components/FlagCountry'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import { FASTLY_PATH } from '~/const/asset'
import { useAppsManagementDetailsQuery } from '~/queries/application-management'
import {
  useSetChangesStatusMutation,
  useSetTreatmentStatusMutation,
  keys as uaCampaignKeys,
  useSetLaterStatusMutation,
} from '~/queries/ua-campaign'
import { resize } from '~/utils/media'
import { copyText } from '~/utils/navigator'
import { displayToast } from '~/utils/toast'
import CommentModal from '~/views/UAManagement/CommentModal'
import { getWarning, NETWORK_LOGO, RECOMMENDATIONS } from '~/views/UAManagement/ua-campaign'

import './styles.scss'

const VariationIcon = ({ recommendation }: { recommendation: string }): ReactElement => {
  const [variation] = recommendation ? recommendation.split('_') : ['']

  switch (variation) {
    case 'increase':
      return <MdArrowUpward color="#00c064" size={16} />
    case 'decrease':
      return <MdArrowDownward color="#ff2e26" size={16} />
    default:
      return <></>
  }
}

interface Props {
  infos: IUaCampaign
}

const UaManagementCard = ({ infos }: Props): ReactElement => {
  /** Local state */

  const {
    additional_data,
    app_key,
    bid,
    changes_made,
    changes_made_by,
    comment,
    comment_at,
    comment_by,
    compute_date,
    country,
    daily_budget,
    device_type,
    do_later,
    do_later_by,
    extrapolation_warning,
    id,
    network,
    new_bid,
    new_budget,
    recommendation,
    treated,
    treated_by,
    ua_adset_id,
    ua_adset_name,
    ua_campaign_id,
    ua_campaign_name,
    ua_campaign_optimization,
    ua_campaign_type,
  } = infos

  const {
    continual_cpi_increase,
    continual_eroas_decrease,
    cpi,
    eltv_d10_last_7d,
    eroas_last_7d,
    has_ads_ltv,
    roas_goal_gap_alerting,
    ua_installs,
    ua_spend,
    x_eroas_dx_last_7d,
  } = additional_data

  const queryClient = useQueryClient()

  const [isGridOpen, setIsGridOpen] = useState(false)

  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)

  /** Queries & Mutations */

  const { data: appData } = useAppsManagementDetailsQuery(app_key)

  const { mutateAsync: setTreatmentStatus } = useSetTreatmentStatusMutation()

  const { mutateAsync: setChangesStatus } = useSetChangesStatusMutation()

  const { mutateAsync: setLaterStatus } = useSetLaterStatusMutation()

  /** Handlers */

  const handleComplementaryInfosClick = () => setIsGridOpen(!isGridOpen)

  const handleCommentClick = () => setIsCommentModalOpen(!isCommentModalOpen)

  const handleTreatedChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (id) {
      try {
        await setTreatmentStatus({ id, treated: e.target.checked })
        await queryClient.invalidateQueries({ queryKey: uaCampaignKeys.list() })
      } catch (e) {
        captureException(e)
        displayToast('Treat value could not be updated', 'error')
      }
    }
  }

  const handleChangesMadeChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (id) {
      try {
        await setChangesStatus({ changesMade: e.target.checked, id })
        await queryClient.invalidateQueries({ queryKey: uaCampaignKeys.list() })
      } catch (e) {
        captureException(e)
        displayToast('Change value could not be updated', 'error')
      }
    }
  }

  const handleDoLaterChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (id) {
      try {
        await setLaterStatus({ doLater: e.target.checked, id })
        await queryClient.invalidateQueries({ queryKey: uaCampaignKeys.list() })
      } catch (e) {
        captureException(e)
        displayToast('Later value could not be updated', 'error')
      }
    }
  }

  const handleCopyId = useCallback(() => copyText(id), [id])

  const handleNavigateToUaDashboard = useCallback(() => {
    const startDate = dayjs(compute_date).subtract(1, 'month').format('YYYY-MM-DD')
    window.open(
      `/ua-dashboard?device_type=${device_type}&end_date=${compute_date}&start_date=${startDate}&ua_adset_id=${ua_adset_id}&ua_campaign_id=${ua_campaign_id}&ua_source=${network}&aggregations%5B%5D=date&app_key%5B%5D=${app_key}&country%5B%5D=${country}`
    )
  }, [app_key, compute_date, country, device_type, network, ua_adset_id, ua_campaign_id])

  /** Render */

  const menu: Menu = useMemo(
    () => ({
      actions: [
        {
          action: () => handleCopyId(),
          label: 'Copy recommendation ID',
        },
      ],
      navigations: [
        {
          action: () => handleNavigateToUaDashboard(),
          label: 'Go to UA Dashboard',
          type: 'external',
        },
      ],
    }),
    [handleCopyId, handleNavigateToUaDashboard]
  )

  return (
    <>
      <div className="ua-management-card">
        <ActionMenu className="ua-management-card__action-menu" iconDirection="vertical" menu={menu} />
        <div className="ua-management-card__top">
          <div className="ua-management-card__top__left">
            <div className="assets">
              <div className="assets__logos">
                {network ? (
                  <img className="assets__logos__network" src={NETWORK_LOGO[network]} title={network} />
                ) : (
                  <div />
                )}
                <AppLogo
                  appLogoUrl={
                    appData?.icon_file_id ? resize(`${FASTLY_PATH}${appData.icon_file_id}`, 32, 32) : undefined
                  }
                  appPlatform={device_type}
                  size="small"
                />
              </div>
              <div className="assets__country">
                <FlagCountry countryCode={country} />
                {country}
              </div>
            </div>
            <div className="campaign-details">
              <div className="campaign-details__title" title={`${ua_campaign_name} - ${ua_adset_name}`}>
                {ua_campaign_name} - {ua_adset_name}
              </div>
              <div className="campaign-details__meta">
                <div className="campaign-details__meta__date">{compute_date}</div>
                <div className="campaign-details__meta__id" onClick={handleCopyId}>
                  • ID: {id}
                  <MdContentCopy className="company-data-ai-product-id__copy-icon" size={9} />
                </div>
              </div>
              <div className="campaign-details__type">
                <Badge value={ua_campaign_type} />
                <Badge value={ua_campaign_optimization} />
              </div>
            </div>
          </div>
          <div className="ua-management-card__top__middle">
            <div className="recommendations">
              <div className="recommendations__infos">
                {recommendation ? <VariationIcon recommendation={recommendation} /> : null}
                <div className="recommendations__infos__recommendation">
                  {recommendation
                    ? RECOMMENDATIONS[recommendation]
                    : !new_budget && !new_bid
                      ? '⚠️ Alert'
                      : '🪄 Update general config'}
                </div>
              </div>
              <div className="recommendations__warning">
                {extrapolation_warning && extrapolation_warning !== 'none' ? (
                  <Badge value={`⚠️ ${getWarning(extrapolation_warning)}`} />
                ) : null}
              </div>
            </div>
            <div className="budget-and-bid">
              {new_budget ? (
                <div className="black-text">
                  Daily budget: <span className="red-text">{daily_budget}</span>
                  <MdArrowRightAlt size={12} />
                  <span className="green-text">{new_budget}</span>
                </div>
              ) : null}
              {new_bid ? (
                <div className="black-text">
                  Bid: <span className="red-text">{bid}</span>
                  <MdArrowRightAlt size={12} />
                  <span className="green-text">{new_bid}</span>
                </div>
              ) : null}
            </div>
          </div>
          <div className="ua-management-card__top__right">
            <div className="actions">
              <div className="checkboxes">
                {treated_by ? <AvatarWithCall userId={treated_by} /> : null}
                <CheckboxInput checked={treated} label="Treated" onChange={handleTreatedChange} />
                {new_budget || new_bid ? (
                  <>
                    {changes_made_by ? <AvatarWithCall userId={changes_made_by} /> : null}
                    <CheckboxInput checked={changes_made} label="Changes made" onChange={handleChangesMadeChange} />
                    {do_later_by ? <AvatarWithCall userId={do_later_by} /> : null}
                    <CheckboxInput checked={do_later} label="Later" onChange={handleDoLaterChange} />
                  </>
                ) : null}
              </div>
              <div className="icons">
                <div className="icon" onClick={handleCommentClick} title={comment || undefined}>
                  {comment ? <MdMessage size={25} /> : <MdOutlineMessage size={25} />}
                </div>
                <div className="icon" onClick={handleComplementaryInfosClick}>
                  <MdOutlineQueryStats size={25} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {isGridOpen ? (
          <div className="ua-management-card__bottom">
            <table className="statistic-table">
              <thead>
                <tr>
                  <th scope="col">ua_spend</th>
                  <th scope="col">ua_installs</th>
                  <th scope="col">cpi</th>
                  <th scope="col">eltv_d10_last_7d</th>
                  <th scope="col">continual_cpi_increase</th>
                  {continual_eroas_decrease != null && <th scope="col">continual_eroas_decrease </th>}
                  {eroas_last_7d != null && <th scope="col">eroas_last_7d </th>}
                  {has_ads_ltv != null && <th scope="col">has_ads_ltv</th>}
                  {roas_goal_gap_alerting != null && <th scope="col">roas_goal_gap_alerting</th>}
                  {x_eroas_dx_last_7d != null && <th scope="col">x_eroas_dx_last_7d</th>}{' '}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{ua_spend?.toFixed(0)}€</td>
                  <td>{ua_installs}</td>
                  <td>{cpi?.toFixed(3)}€</td>
                  <td>{eltv_d10_last_7d?.toFixed(3)}€</td>
                  <td className="text-centered">{continual_cpi_increase ? '✅' : '❌'}</td>
                  {continual_eroas_decrease != null ? (
                    <td className="text-centered">{continual_eroas_decrease ? '✅' : '❌'}</td>
                  ) : null}
                  {eroas_last_7d != null ? <td>{(eroas_last_7d * 100).toFixed(2)}%</td> : null}
                  {has_ads_ltv != null ? <td className="text-centered">{has_ads_ltv ? '✅' : '❌'}</td> : null}
                  {roas_goal_gap_alerting != null ? <td>{roas_goal_gap_alerting}</td> : null}
                  {x_eroas_dx_last_7d != null ? <td>{(x_eroas_dx_last_7d * 100).toFixed(2)}%</td> : null}
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
      <CommentModal
        author={comment_by}
        date={comment_at}
        defaultValue={comment || ''}
        id={id}
        isOpen={isCommentModalOpen}
        onClose={() => setIsCommentModalOpen(false)}
      />
    </>
  )
}

export default UaManagementCard
